import React, { useState, useEffect, useRef } from 'react';
import { 
  X, 
  Search, 
  Users, 
  User, 
  Calendar, 
  Percent, 
  DollarSign,
  AlertCircle,
  CheckCircle,
  Calculator,
  Loader2,
  Check,
  CreditCard,
  PartyPopper
} from 'lucide-react';
import { Button } from './ui/button';
import { Toast } from './ui/Toast';
import { LongToast, shouldUseLongToast } from './ui/LongToast';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import ConfirmDialog from './ConfirmDialog';
import { api } from '../services/api';

// ========== COMPOSANT BADGE DE STATUT ==========
const StatutBadge = ({ statut, statutFrequentation }) => {
  if (statutFrequentation === 'non_scolarise') {
    return (
      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-gray-500 text-white">
        <AlertCircle size={14} />
        Non scolarisé
      </span>
    );
  }
  
  const configs = {
    'Payé': {
      bg: 'bg-green-500',
      text: 'text-white',
      icon: <CheckCircle size={14} />
    },
    'Payé au prorata': {
      bg: 'bg-blue-500',
      text: 'text-white',
      icon: <Calculator size={14} />
    },
    'Partiel': {
      bg: 'bg-orange-500',
      text: 'text-white',
      icon: <AlertCircle size={14} />
    },
    'Non payé': {
      bg: 'bg-red-500',
      text: 'text-white',
      icon: <AlertCircle size={14} />
    }
  };
  
  const config = configs[statut] || configs['Non payé'];
  
  return (
    <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${config.bg} ${config.text}`}>
      {config.icon}
      {statut}
    </span>
  );
};




const ModalPaiementProrata = ({ 
  isOpen, 
  onClose, 
  anneeActive,
  moisScolaires,
  onSuccess 
}) => {
  // ========== ÉTATS TOAST ==========
  const [toasts, setToasts] = useState([]);
  // Le serveur refuse un prorata sur un mois engagé sauf demande explicite.
  // L'écran doit donc porter la même décision, et la poser avant la saisie.
  const [forcerSurMoisEngage, setForcerSurMoisEngage] = useState(false);

  const showToast = (message, type = 'success') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    
    // Auto-remove après 5 secondes
    setTimeout(() => {
      removeToast(id);
    }, 5000);
  };

  const removeToast = (id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };

  // ========== ÉTATS ==========
  const [typeSelection, setTypeSelection] = useState('eleve');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  

  
  // Recherche
  const [searchTerm, setSearchTerm] = useState('');
  const [resultatsRecherche, setResultatsRecherche] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  
  const searchContainerRef = useRef(null);


  // Sélection
  const [eleveSelectionne, setEleveSelectionne] = useState(null);
  const [familleSelectionnee, setFamilleSelectionnee] = useState(null);
  const [elevesFamily, setElevesFamily] = useState([]);
  const [elevesSelectionnesDansFamily, setElevesSelectionnesDansFamily] = useState([]);
  
  // Paramètres du prorata
  const [moisSelectionne, setMoisSelectionne] = useState(null);
  const [modeCalcul, setModeCalcul] = useState('pourcentage');
  const [pourcentage, setPourcentage] = useState(50);
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');
  const [montantFixe, setMontantFixe] = useState('');
  const [justification, setJustification] = useState('');
  const [reporterTropPercu, setReporterTropPercu] = useState(true);
  
  // Paiement
  const [marquerCommePayé, setMarquerCommePayé] = useState(false);
  const [modePaiementId, setModePaiementId] = useState('');
  const [referencePaiement, setReferencePaiement] = useState('');
  const [modesPaiement, setModesPaiement] = useState([]);
  
  // Données récupérées
  const [montantNormal, setMontantNormal] = useState(0);
  const [montantDejaPaye, setMontantDejaPaye] = useState(0);
  const [paiementDetails, setPaiementDetails] = useState(null);

  // ConfirmDialog
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);

  // ========== CHARGER MODES DE PAIEMENT ==========
  useEffect(() => {
    const chargerModesPaiement = async () => {
      try {
        const response = await api.paiements.getModesPaiement();
        if (response.success) {
          setModesPaiement(response.data);
        }
      } catch (error) {
        console.error('Erreur chargement modes de paiement:', error);
      }
    };
    
    if (isOpen) {
      chargerModesPaiement();
    }
  }, [isOpen]);



  // ========== FERMETURE AUTOCOMPLETE AU CLIC EXTÉRIEUR ==========
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setResultatsRecherche([]);
      }
    };

    if (resultatsRecherche.length > 0) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [resultatsRecherche]);




  // ========== ORGANISER LES MOIS PAR TRIMESTRE ==========
  const moisParTrimestre = moisScolaires.reduce((acc, mois) => {
    const trimestre = mois.trimestre;
    if (!acc[trimestre]) {
      acc[trimestre] = [];
    }
    acc[trimestre].push(mois);
    return acc;
  }, {});

  // ========== FONCTIONS UTILITAIRES ==========
  const formatMontant = (montant) => {
    return `${Math.round(montant).toLocaleString('fr-FR')} FCFA`;
  };

  const calculerMontantProrata = () => {
    let montant = montantNormal;
    
    switch (modeCalcul) {
      case 'pourcentage':
        montant = montantNormal * (pourcentage / 100);
        break;
        
      case 'periode':
        if (dateDebut && dateFin) {
          const debut = new Date(dateDebut);
          const fin = new Date(dateFin);
          const joursPresents = Math.ceil((fin - debut) / (1000 * 60 * 60 * 24)) + 1;
          const joursDansMois = 30;
          montant = montantNormal * (joursPresents / joursDansMois);
        }
        break;
        
      case 'montant_fixe':
        montant = parseFloat(montantFixe) || 0;
        break;
    }
    
    return Math.round(montant);
  };

  // Mêmes statuts que `isPaiementVerrouille` côté serveur : un mois est engagé
  // dès qu'un franc est encaissé, ou que son statut le dit.
  const moisDejaEngage = (() => {
    const p = paiementDetails?.paiement;
    if (!p) return false;
    if (parseFloat(p.montant_paye || 0) > 0) return true;
    const statut = String(p.statut || '').toLowerCase();
    return statut.startsWith('pay') || statut.includes('partiel')
      || statut.includes('prorata') || statut.includes('sold');
  })();

  const calculerTropPercu = () => {
    const prorata = calculerMontantProrata();
    return Math.max(0, montantDejaPaye - prorata);
  };


  // ========== RECHERCHE D'ÉLÈVES ==========
  const rechercherEleves = async (term) => {
    if (term.length < 2) {
      setResultatsRecherche([]);
      return;
    }

    setIsSearching(true);
    try {
      const response = await api.eleves.rechercher(term, anneeActive.id);
      if (response.success) {
        setResultatsRecherche(response.data);
      }
    } catch (error) {
      console.error('Erreur recherche élèves:', error);
      showToast('Erreur lors de la recherche des élèves', 'error');
    } finally {
      setIsSearching(false);
    }
  };

  // ========== RECHERCHE DE FAMILLES ==========
  const rechercherFamilles = async (term) => {
    if (term.length < 2) {
      setResultatsRecherche([]);
      return;
    }

    setIsSearching(true);
    try {
      const response = await api.familles.rechercher(term);
      if (response.success) {
        setResultatsRecherche(response.data);
      }
    } catch (error) {
      console.error('Erreur recherche familles:', error);
      showToast('Erreur lors de la recherche des familles', 'error');
    } finally {
      setIsSearching(false);
    }
  };

  // ========== CHARGER LES INFOS DE PAIEMENT ==========
  const chargerInfosPaiement = async (eleveId, moisId) => {
    setLoading(true);
    try {
      const response = await api.eleves.getPaiementMois(eleveId, moisId, anneeActive.id);
      if (response.success) {
        setPaiementDetails(response.data);
        setMontantNormal(response.data.paiement.montant_attendu || 0);
        setMontantDejaPaye(response.data.paiement.montant_paye || 0);
      }
    } catch (error) {
      console.error('Erreur chargement infos paiement:', error);
      showToast('Erreur lors du chargement des informations de paiement', 'error');
    } finally {
      setLoading(false);
    }
  };

  // ========== CHARGER INFOS PAIEMENT FAMILLE ==========
  const chargerInfosPaiementFamille = async () => {
    setLoading(true);
    try {
      let montantTotalNormal = 0;
      let montantTotalDejaPaye = 0;
      const statutsEleves = [];
      let tousNonScolarises = true;
      let auMoinsUnPartiel = false;
      let auMoinsUnNonPaye = false;
      let auMoinsUnProrata = false;
      
      for (const eleveId of elevesSelectionnesDansFamily) {
        const eleve = elevesFamily.find(e => e.id === eleveId);
        const response = await api.eleves.getPaiementMois(eleveId, moisSelectionne, anneeActive.id);
        
        if (response.success) {
          const paiement = response.data.paiement;
          
          if (paiement.prorata_applique) {
            auMoinsUnProrata = true;
          }
          
          if (paiement.statut_frequentation !== 'non_scolarise') {
            montantTotalNormal += parseFloat(paiement.montant_attendu || 0);
            montantTotalDejaPaye += parseFloat(paiement.montant_paye || 0);
            tousNonScolarises = false;
            
            if (paiement.statut === 'Partiel') auMoinsUnPartiel = true;
            if (paiement.statut === 'Non payé') auMoinsUnNonPaye = true;
          }
          
          statutsEleves.push({
            eleve_id: eleveId,
            eleve_nom: `${eleve.prenom} ${eleve.nom}`,
            statut: paiement.statut,
            statut_frequentation: paiement.statut_frequentation,
            montant_attendu: paiement.montant_attendu,
            montant_paye: paiement.montant_paye,
            prorata_applique: paiement.prorata_applique
          });
        }
      }
      
      let statutGlobal = 'Payé';
      if (tousNonScolarises) {
        statutGlobal = 'Non scolarisé';
      } else if (auMoinsUnNonPaye || montantTotalDejaPaye === 0) {
        statutGlobal = 'Non payé';
      } else if (auMoinsUnPartiel || montantTotalDejaPaye < montantTotalNormal) {
        statutGlobal = 'Partiel';
      }
      
      setMontantNormal(montantTotalNormal);
      setMontantDejaPaye(montantTotalDejaPaye);
      setPaiementDetails({
        paiement: {
          montant_attendu: montantTotalNormal,
          montant_paye: montantTotalDejaPaye,
          statut: statutGlobal,
          statut_frequentation: tousNonScolarises ? 'non_scolarise' : 'scolarise',
          prorata_applique: auMoinsUnProrata,
          eleves_details: statutsEleves
        }
      });
      
    } catch (error) {
      console.error('Erreur chargement infos paiement famille:', error);
      showToast('Erreur lors du chargement des informations de paiement', 'error');
    } finally {
      setLoading(false);
    }
  };

  // ========== SÉLECTION ÉLÈVE ==========
  const handleSelectEleve = async (eleve) => {
    setEleveSelectionne(eleve);
    setResultatsRecherche([]);
    setSearchTerm('');
    setMoisSelectionne(null);
    setPaiementDetails(null);
  };

  // ========== SÉLECTION FAMILLE ==========
  const handleSelectFamille = async (famille) => {
    setFamilleSelectionnee(famille);
    setResultatsRecherche([]);
    setSearchTerm('');
    setMoisSelectionne(null);
    
    setLoading(true);
    try {
      const response = await api.familles.getDetailsComplets(famille.id, anneeActive.id);
      if (response.success) {
        setElevesFamily(response.data.eleves);
        setElevesSelectionnesDansFamily(response.data.eleves.map(e => e.id));
      }
    } catch (error) {
      console.error('Erreur chargement famille:', error);
      showToast('Erreur lors du chargement de la famille', 'error');
    } finally {
      setLoading(false);
    }
  };

  // ========== CHANGEMENT DE MOIS ==========
  useEffect(() => {
    if (moisSelectionne) {
      if (typeSelection === 'eleve' && eleveSelectionne) {
        chargerInfosPaiement(eleveSelectionne.id, moisSelectionne);
      } else if (typeSelection === 'famille' && familleSelectionnee && elevesSelectionnesDansFamily.length > 0) {
        chargerInfosPaiementFamille();
      }
    }
  }, [moisSelectionne, eleveSelectionne, familleSelectionnee, elevesSelectionnesDansFamily]);


  // ========== VALIDATION ==========
  const peutEnregistrer = () => {
    if (!moisSelectionne) return false;
    if (!eleveSelectionne && !familleSelectionnee) return false;
    if (!justification.trim()) return false;
    if (paiementDetails?.paiement?.prorata_applique) return false;
    if (paiementDetails?.paiement?.statut_frequentation === 'non_scolarise') return false;
    
    if (modeCalcul === 'periode' && (!dateDebut || !dateFin)) return false;
    if (modeCalcul === 'montant_fixe' && !montantFixe) return false;
    
    if (marquerCommePayé && !modePaiementId) return false;

    // Sur un mois engagé, le bouton reste inerte tant que la case n'est pas
    // cochée : le serveur refuserait, autant le dire avant l'envoi.
    if (moisDejaEngage && !forcerSurMoisEngage) return false;

    return true;
  };

  // ========== ENREGISTREMENT ==========
  const handleSave = async () => {
    if (!peutEnregistrer()) {
      showToast('Veuillez remplir tous les champs obligatoires', 'error');
      return;
    }

    setSaving(true);
    try {
      const montantProrata = calculerMontantProrata();
      const tropPercu = calculerTropPercu();
      
      const data = {
        annee_scolaire_id: anneeActive.id,
        mois_id: moisSelectionne,
        mode_calcul: modeCalcul,
        valeur_calcul: modeCalcul === 'pourcentage' ? pourcentage 
                     : modeCalcul === 'periode' ? `${dateDebut}|${dateFin}`
                     : montantFixe,
        montant_prorata: montantProrata,
        forcer_sur_mois_engage: forcerSurMoisEngage,
        justification: justification.trim(),
        reporter_trop_percu: reporterTropPercu,
        trop_percu: tropPercu,
        marquer_comme_paye: marquerCommePayé,
        mode_paiement_id: marquerCommePayé ? modePaiementId : null,
        reference_paiement: marquerCommePayé ? referencePaiement.trim() : null
      };

      let response;
      if (typeSelection === 'eleve') {
        response = await api.eleves.appliquerProrata(eleveSelectionne.id, data);
      } else {
        response = await api.familles.appliquerProrata(familleSelectionnee.id, {
          ...data,
          eleves_ids: elevesSelectionnesDansFamily
        });
      }

       if (response.success) {
        // ✅ Afficher UNIQUEMENT le toast
        const message = marquerCommePayé 
          ? 'Le prorata a été appliqué et le paiement a été enregistré avec succès !' 
          : 'Le prorata a été appliqué avec succès !';
        
        showToast(message, 'success');
        
        // ✅ Recharger les données SANS fermer le modal
        setTimeout(async () => {
          if (typeSelection === 'eleve') {
            await chargerInfosPaiement(eleveSelectionne.id, moisSelectionne);
          } else {
            await chargerInfosPaiementFamille();
          }
          
          // Réinitialiser le formulaire
          setJustification('');
          setMarquerCommePayé(false);
          setModePaiementId('');
          setReferencePaiement('');
          
          // ✅ Commenter pour éviter que le parent ferme le modal
          // onSuccess(response.data);
        }, 1000);
        
      } else {
        showToast(response.message || 'Erreur lors de l\'enregistrement', 'error');
      }
    } catch (error) {
      console.error('Erreur enregistrement prorata:', error);
      showToast('Erreur lors de l\'enregistrement du prorata', 'error');
    } finally {
      setSaving(false);
    }
  };

  // ========== ANNULATION DU PRORATA ==========
  const handleAnnulerProrata = () => {
    setConfirmAction(() => async () => {
      setSaving(true);
      try {
        let response;
        
        if (typeSelection === 'eleve') {
          response = await api.eleves.annulerProrata(
            eleveSelectionne.id,
            moisSelectionne,
            anneeActive.id
          );
        } else {
          response = await api.familles.annulerProrata(
            familleSelectionnee.id,
            moisSelectionne,
            anneeActive.id,
            elevesSelectionnesDansFamily
          );
        }

        if (response.success) {
          // ✅ Afficher UNIQUEMENT le toast
          showToast('Prorata annulé avec succès', 'success');
          
          // ✅ Recharger les données SANS fermer le modal
          setTimeout(async () => {
            if (typeSelection === 'eleve') {
              await chargerInfosPaiement(eleveSelectionne.id, moisSelectionne);
            } else {
              await chargerInfosPaiementFamille();
            }
            
            // ✅ Commenter pour éviter que le parent ferme le modal
            // onSuccess(response.data);
          }, 1000);
          
        } else {
          showToast(response.message || 'Erreur lors de l\'annulation du prorata', 'error');
        }
      } catch (error) {
        console.error('Erreur annulation prorata:', error);
        showToast('Erreur lors de l\'annulation du prorata', 'error');
      } finally {
        setSaving(false);
      }
    });
    setShowConfirmDialog(true);
  };

  // ========== RENDU ==========
  if (!isOpen) return null;

  const montantProrata = calculerMontantProrata();
  const tropPercu = calculerTropPercu();

  return (
    <>
      {/* 🆕 Modal plus large : max-w-6xl */}
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-y-auto relative">
          {/* Header */}
          <div className="sticky top-0 bg-[#246990] text-white p-5 flex items-center justify-between z-10">
            <div className="flex items-center gap-3">
              <Calculator size={24} />
              <h2 className="text-xl font-bold">Paiement Au Prorata</h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X size={22} />
            </button>
          </div>

          {/* Bande d'information complète */}
          <div className="bg-blue-50 border-b-2 border-blue-200 p-4">
            <div className="flex items-start gap-3">
              <div className="bg-[#246990] p-2.5 rounded-full flex-shrink-0">
                <AlertCircle className="text-white" size={20} />
              </div>
              <div className="flex-1">
                
                <p className="text-gray-700 text-sm leading-relaxed mb-3">
                  Le <strong>paiement au prorata</strong> permet d'ajuster les frais scolaires d'un élève ou d'une famille 
                  lorsque la scolarité n'a pas été complète sur un mois donné.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
                  <div className="bg-white p-3 rounded-lg border border-blue-200">
                    <div className="flex items-center gap-2 mb-1">
                      <User className="text-[#246990] flex-shrink-0" size={16} />
                      <span className="font-semibold text-sm text-gray-800">Maladie / Absence</span>
                    </div>
                    <p className="text-xs text-gray-600">
                      Élève malade ou absent pendant une partie du mois
                    </p>
                  </div>
                  
                  <div className="bg-white p-3 rounded-lg border border-blue-200">
                    <div className="flex items-center gap-2 mb-1">
                      <Calendar className="text-[#246990] flex-shrink-0" size={16} />
                      <span className="font-semibold text-sm text-gray-800">Arrivée en cours de mois</span>
                    </div>
                    <p className="text-xs text-gray-600">
                      Nouvel élève inscrit en milieu de mois
                    </p>
                  </div>
                  
                  <div className="bg-white p-3 rounded-lg border border-blue-200">
                    <div className="flex items-center gap-2 mb-1">
                      <Calculator className="text-[#246990] flex-shrink-0" size={16} />
                      <span className="font-semibold text-sm text-gray-800">Départ anticipé</span>
                    </div>
                    <p className="text-xs text-gray-600">
                      Élève quittant l'école avant la fin du mois
                    </p>
                  </div>
                </div>

                <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="text-yellow-600 flex-shrink-0 mt-0.5" size={16} />
                    <p className="text-xs text-yellow-800">
                      <strong>Important :</strong> Le prorata ajuste le montant attendu pour le mois concerné. 
                      Si un paiement a déjà été effectué, un <strong>trop-perçu</strong> peut être généré et reporté 
                      automatiquement sur le mois suivant.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Content - Layout progressif */}
          <div className="p-6 space-y-6 overflow-visible">

            {/* ÉTAPE 1 : SÉLECTION */}
            <div>
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <span className="bg-[#246990] text-white w-8 h-8 rounded-full flex items-center justify-center text-sm">1</span>
                Sélection
              </h3>

              {/* Type de sélection */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <button
                  onClick={() => {
                    setTypeSelection('eleve');
                    setEleveSelectionne(null);
                    setFamilleSelectionnee(null);
                    setSearchTerm('');
                    setMoisSelectionne(null);
                    setPaiementDetails(null);
                  }}
                  className={`p-5 rounded-lg border-2 transition-all ${
                    typeSelection === 'eleve'
                      ? 'border-[#246990] bg-[#246990]/10 shadow-md'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <User size={28} className={`mx-auto mb-2 ${typeSelection === 'eleve' ? 'text-[#246990]' : 'text-gray-600'}`} />
                  <div className="font-semibold">Élève individuel</div>
                </button>

                <button
                  onClick={() => {
                    setTypeSelection('famille');
                    setEleveSelectionne(null);
                    setFamilleSelectionnee(null);
                    setSearchTerm('');
                    setMoisSelectionne(null);
                    setPaiementDetails(null);
                  }}
                  className={`p-5 rounded-lg border-2 transition-all ${
                    typeSelection === 'famille'
                      ? 'border-[#246990] bg-[#246990]/10 shadow-md'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <Users size={28} className={`mx-auto mb-2 ${typeSelection === 'famille' ? 'text-[#246990]' : 'text-gray-600'}`} />
                  <div className="font-semibold">Famille nombreuse</div>
                </button>
              </div>


                  
              {/* Recherche avec autocomplete */}
              <div className="relative" ref={searchContainerRef}>
                <Search className="absolute left-3 top-3 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder={typeSelection === 'eleve' ? 'Rechercher un élève...' : 'Rechercher une famille...'}
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    if (typeSelection === 'eleve') {
                      rechercherEleves(e.target.value);
                    } else {
                      rechercherFamilles(e.target.value);
                    }
                  }}
                  className="w-full pl-11 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#246990] focus:outline-none"
                />

                {/* Résultats de recherche */}
                {resultatsRecherche.length > 0 && (
                  <div className="absolute left-0 right-0 mt-1 bg-white border-2 border-gray-200 rounded-lg shadow-2xl max-h-64 overflow-y-auto z-[9]">
                    {resultatsRecherche.map((item) => (
                      <div
                        key={item.id}
                        onClick={() => {
                          if (typeSelection === 'eleve') {
                            handleSelectEleve(item);
                          } else {
                            handleSelectFamille(item);
                          }
                        }}
                        className="p-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0 transition-colors"
                      >
                        {typeSelection === 'eleve' ? (
                          <div>
                            <div className="font-semibold">{item.prenom} {item.nom}</div>
                            <div className="text-xs text-gray-600">{item.classe_nom} • {item.matricule}</div>
                          </div>
                        ) : (
                          <div>
                            <div className="font-semibold">Famille {item.nom}</div>
                            <div className="text-xs text-gray-600">{item.nombre_enfants} enfants</div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            

              {/* Affichage sélection élève */}
              {eleveSelectionne && (
                <div className="mt-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-semibold text-lg">{eleveSelectionne.prenom} {eleveSelectionne.nom}</div>
                      <div className="text-sm text-gray-600">{eleveSelectionne.classe_nom} • {eleveSelectionne.matricule}</div>
                    </div>
                    <CheckCircle className="text-green-600" size={24} />
                  </div>
                </div>
              )}

              {/* Affichage sélection famille */}
              {familleSelectionnee && (
                <div className="mt-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <div className="font-semibold text-lg">Famille {familleSelectionnee.nom}</div>
                      <div className="text-sm text-gray-600">{elevesFamily.length} élèves</div>
                    </div>
                    <CheckCircle className="text-green-600" size={24} />
                  </div>

                  {elevesFamily.length > 0 && (
                    <div className="mt-3 space-y-2">
                      <div className="text-sm font-medium text-gray-700 mb-2">Élèves concernés :</div>
                      {elevesFamily.map((eleve) => (
                        <label 
                          key={eleve.id} 
                          className="flex items-center p-2 hover:bg-white rounded cursor-pointer transition-colors"
                        >
                          <div 
                            className={`w-5 h-5 rounded border-2 flex items-center justify-center mr-3 transition-all ${
                              elevesSelectionnesDansFamily.includes(eleve.id)
                                ? 'bg-[#246990] border-[#246990]'
                                : 'border-gray-300 hover:border-gray-400'
                            }`}
                            onClick={(e) => {
                              e.preventDefault();
                              if (elevesSelectionnesDansFamily.includes(eleve.id)) {
                                setElevesSelectionnesDansFamily(elevesSelectionnesDansFamily.filter(id => id !== eleve.id));
                              } else {
                                setElevesSelectionnesDansFamily([...elevesSelectionnesDansFamily, eleve.id]);
                              }
                            }}
                          >
                            {elevesSelectionnesDansFamily.includes(eleve.id) && (
                              <Check size={14} className="text-white" />
                            )}
                          </div>
                          <span className="text-sm">{eleve.prenom} {eleve.nom} - {eleve.classe_nom}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* ÉTAPE 2 : MOIS */}
            {(eleveSelectionne || familleSelectionnee) && (
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <span className="bg-[#246990] text-white w-8 h-8 rounded-full flex items-center justify-center text-sm">2</span>
                  Mois concerné
                </h3>

                <div className="space-y-4">
                  {Object.keys(moisParTrimestre).sort().map((trimestre) => (
                    <div key={trimestre} className="border border-gray-200 rounded-lg p-4">
                      <h4 className="font-semibold text-sm text-gray-700 mb-3 flex items-center gap-2">
                        <Calendar size={18} />
                        Trimestre {trimestre}
                      </h4>
                      <div className="grid grid-cols-3 gap-3">
                        {moisParTrimestre[trimestre].map((mois) => (
                          <button
                            key={mois.id}
                            onClick={() => setMoisSelectionne(mois.id)}
                            className={`flex items-center justify-center gap-2 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                              moisSelectionne === mois.id
                                ? 'border-[#246990] bg-[#246990]/10 shadow-md'
                                : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                            }`}
                          >
                            <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                              moisSelectionne === mois.id
                                ? 'border-[#246990] bg-[#246990]'
                                : 'border-gray-400'
                            }`}>
                              {moisSelectionne === mois.id && (
                                <div className="w-2 h-2 rounded-full bg-white"></div>
                              )}
                            </div>
                            <span className={`text-sm font-medium ${moisSelectionne === mois.id ? 'text-[#246990]' : 'text-gray-700'}`}>
                              {mois.nom}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

         {/* ÉTAPE 3 : STATUT ACTUEL */}
            {moisSelectionne && paiementDetails && (
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <span className="bg-[#246990] text-white w-8 h-8 rounded-full flex items-center justify-center text-sm">3</span>
                  Statut actuel
                </h3>

                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="animate-spin text-[#246990]" size={32} />
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                      <div className="text-sm text-gray-600 mb-1">Montant attendu</div>
                      <div className="text-2xl font-bold text-gray-900">{formatMontant(montantNormal)}</div>
                    </div>
                    
                    <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                      <div className="text-sm text-gray-600 mb-1">Montant déjà payé</div>
                      <div className="text-2xl font-bold text-green-600">{formatMontant(montantDejaPaye)}</div>
                    </div>
                    
                    <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                      <div className="text-sm text-gray-600 mb-2">Statut</div>
                      <StatutBadge 
                        statut={paiementDetails.paiement.statut} 
                        statutFrequentation={paiementDetails.paiement.statut_frequentation}
                      />
                    </div>
                  </div>
                )}

                {/* Alerte si prorata déjà appliqué */}
                {/* Le mois est deja engage : on le dit ICI, au moment ou l'ecran
                    montre le statut, et non au moment d'enregistrer.
                    Sans cet avertissement, l'operateur configurait tout son
                    prorata, decouvrait un « trop-percu » qu'il n'avait pas
                    demande - c'est simplement ce qu'il a deja paye moins le
                    nouveau montant - puis se faisait refuser a l'envoi. */}
                {Boolean(paiementDetails?.paiement) && moisDejaEngage && (
                  <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="text-amber-600 flex-shrink-0 mt-0.5" size={20} />
                      <div className="flex-1">
                        <h4 className="font-semibold text-amber-900 mb-1">Ce mois est déjà réglé</h4>
                        <p className="text-sm text-amber-800">
                          {formatMontant(montantDejaPaye)} ont été encaissés sur{' '}
                          {formatMontant(paiementDetails?.paiement?.montant_attendu)} dus.
                          Appliquer un prorata réécrira le montant attendu : si le nouveau montant est
                          plus bas, la différence deviendra un trop-perçu à reporter ou à rembourser.
                        </p>
                        <label className="mt-3 flex cursor-pointer items-start gap-2">
                          <input
                            type="checkbox"
                            checked={forcerSurMoisEngage}
                            onChange={(e) => setForcerSurMoisEngage(e.target.checked)}
                            className="mt-0.5"
                          />
                          <span className="text-sm font-medium text-amber-900">
                            J'applique quand même le prorata sur ce mois réglé
                          </span>
                        </label>
                      </div>
                    </div>
                  </div>
                )}

                {/* Boolean() volontaire : `prorata_applique` est un TINYINT(1) que
                    mysql2 renvoie comme le NOMBRE 0, pas comme false. Or `0 && …`
                    vaut 0 - que React affiche, contrairement à false qu'il ignore.
                    D'où le « 0 » qui apparaissait sous Statut actuel sur tout mois
                    sans prorata. */}
                {Boolean(paiementDetails?.paiement?.prorata_applique) && (
                  <div className="mt-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="text-orange-600 flex-shrink-0 mt-0.5" size={20} />
                      <div className="flex-1">
                        <h4 className="font-semibold text-orange-800 mb-1">Prorata déjà appliqué</h4>
                        <p className="text-sm text-orange-700 mb-3">
                          Un prorata a déjà été appliqué sur ce mois. Vous pouvez l'annuler pour en appliquer un nouveau.
                        </p>
                        <Button
                          onClick={handleAnnulerProrata}
                          disabled={saving}
                          className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 text-sm flex items-center gap-2"
                        >
                          {saving ? (
                            <>
                              <Loader2 className="animate-spin" size={16} />
                              Annulation...
                            </>
                          ) : (
                            <>
                              <X size={16} />
                              Annuler le prorata
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Alerte si non scolarisé */}
                {paiementDetails?.paiement?.statut_frequentation === 'non_scolarise' && (
                  <div className="mt-4 p-4 bg-gray-50 border border-gray-300 rounded-lg">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="text-gray-600 flex-shrink-0 mt-0.5" size={20} />
                      <div>
                        <h4 className="font-semibold text-gray-800 mb-1">Élève non scolarisé</h4>
                        <p className="text-sm text-gray-600">
                          Cet élève est marqué comme non scolarisé pour ce mois. Impossible d'appliquer un prorata.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ÉTAPE 4 : MODE DE CALCUL */}
            {moisSelectionne && paiementDetails && 
             !paiementDetails?.paiement?.prorata_applique && 
             paiementDetails?.paiement?.statut_frequentation !== 'non_scolarise' && (
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <span className="bg-[#246990] text-white w-8 h-8 rounded-full flex items-center justify-center text-sm">4</span>
                  Mode de calcul
                </h3>

                {/* Sélection du mode */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <button
                    onClick={() => setModeCalcul('pourcentage')}
                    className={`p-4 rounded-lg border-2 transition-all text-left ${
                      modeCalcul === 'pourcentage'
                        ? 'border-[#246990] bg-[#246990]/10'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <Percent className={`mb-2 ${modeCalcul === 'pourcentage' ? 'text-[#246990]' : 'text-gray-600'}`} size={24} />
                    <div className="font-semibold text-sm">Pourcentage</div>
                    <div className="text-xs text-gray-600 mt-1">Réduction en %</div>
                  </button>

                  <button
                    onClick={() => setModeCalcul('periode')}
                    className={`p-4 rounded-lg border-2 transition-all text-left ${
                      modeCalcul === 'periode'
                        ? 'border-[#246990] bg-[#246990]/10'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <Calendar className={`mb-2 ${modeCalcul === 'periode' ? 'text-[#246990]' : 'text-gray-600'}`} size={24} />
                    <div className="font-semibold text-sm">Période</div>
                    <div className="text-xs text-gray-600 mt-1">Dates de présence</div>
                  </button>

                  <button
                    onClick={() => setModeCalcul('montant_fixe')}
                    className={`p-4 rounded-lg border-2 transition-all text-left ${
                      modeCalcul === 'montant_fixe'
                        ? 'border-[#246990] bg-[#246990]/10'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <CreditCard className={`mb-2 ${modeCalcul === 'montant_fixe' ? 'text-[#246990]' : 'text-gray-600'}`} size={24} />
                    <div className="font-semibold text-sm">Montant fixe</div>
                    <div className="text-xs text-gray-600 mt-1">Montant personnalisé</div>
                  </button>
                </div>

                {/* Formulaire selon le mode */}
                <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                  {modeCalcul === 'pourcentage' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Pourcentage de réduction
                      </label>
                      <div className="flex items-center gap-4">
                        <input
                          type="range"
                          min="0"
                          max="100"
                          value={pourcentage}
                          onChange={(e) => setPourcentage(parseInt(e.target.value))}
                          className="flex-1"
                        />
                        <div className="flex items-center gap-2 w-32">
                          <input
                            type="number"
                            min="0"
                            max="100"
                            value={pourcentage}
                            onChange={(e) => setPourcentage(Math.min(100, Math.max(0, parseInt(e.target.value) || 0)))}
                            className="w-20 px-3 py-2 border border-gray-300 rounded-lg text-center focus:border-[#246990] focus:outline-none"
                          />
                          <span className="text-lg font-bold">%</span>
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 mt-2">
                        Le montant sera réduit de {pourcentage}%
                      </p>
                    </div>
                  )}

                  {modeCalcul === 'periode' && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Date de début
                        </label>
                        <input
                          type="date"
                          value={dateDebut}
                          onChange={(e) => setDateDebut(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-[#246990] focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Date de fin
                        </label>
                        <input
                          type="date"
                          value={dateFin}
                          onChange={(e) => setDateFin(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-[#246990] focus:outline-none"
                        />
                      </div>
                      {dateDebut && dateFin && (
                        <p className="text-sm text-gray-600 col-span-2">
                          Période de présence : {Math.ceil((new Date(dateFin) - new Date(dateDebut)) / (1000 * 60 * 60 * 24)) + 1} jours sur 30
                        </p>
                      )}
                    </div>
                  )}

                  {modeCalcul === 'montant_fixe' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Montant à payer
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={montantFixe}
                        onChange={(e) => setMontantFixe(e.target.value)}
                        placeholder="Ex: 15000"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-[#246990] focus:outline-none"
                      />
                    </div>
                  )}
                </div>

                {/* Aperçu du calcul */}
                {montantProrata > 0 && (
                  <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <h4 className="font-semibold text-sm text-gray-700 mb-3">Aperçu du calcul</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>Montant normal :</span>
                        <span className="font-semibold">{formatMontant(montantNormal)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span>Montant prorata :</span>
                        <span className="font-semibold text-[#246990]">{formatMontant(montantProrata)}</span>
                      </div>
                      <div className="flex justify-between text-sm pt-2 border-t border-blue-300">
                        <span className="font-bold">Différence :</span>
                        <span className="font-bold text-green-600">-{formatMontant(montantNormal - montantProrata)}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ÉTAPE 5 : JUSTIFICATION */}
            {moisSelectionne && paiementDetails && 
             !paiementDetails?.paiement?.prorata_applique && 
             paiementDetails?.paiement?.statut_frequentation !== 'non_scolarise' && (
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <span className="bg-[#246990] text-white w-8 h-8 rounded-full flex items-center justify-center text-sm">5</span>
                  Justification
                </h3>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Motif du prorata <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={justification}
                      onChange={(e) => setJustification(e.target.value)}
                      placeholder="Ex: Élève absent du 15 au 25 pour raisons médicales"
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-[#246990] focus:outline-none resize-none"
                    />
                  </div>

                  {/* Gestion du trop-perçu */}
                  {montantDejaPaye > 0 && tropPercu > 0 && (
                    <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <div className="flex items-start gap-3 mb-3">
                        <AlertCircle className="text-yellow-600 flex-shrink-0 mt-0.5" size={20} />
                        <div className="flex-1">
                          <h4 className="font-semibold text-yellow-800 mb-1">Trop-perçu détecté</h4>
                          <p className="text-sm text-yellow-700 mb-2">
                            Un montant de <strong>{formatMontant(tropPercu)}</strong> a été payé en trop. 
                            Souhaitez-vous le reporter sur le mois suivant ?
                          </p>
                        </div>
                      </div>

                      <label className="flex items-center gap-2 p-3 bg-white border border-yellow-300 rounded-lg cursor-pointer hover:bg-yellow-50 transition-colors">
                        <div 
                          className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                            reporterTropPercu
                              ? 'bg-[#246990] border-[#246990]'
                              : 'border-gray-300'
                          }`}
                          onClick={() => setReporterTropPercu(!reporterTropPercu)}
                        >
                          {reporterTropPercu && (
                            <Check size={14} className="text-white" />
                          )}
                        </div>
                        <div className="flex-1">
                          <span className="text-sm font-semibold text-gray-800">
                            Reporter le trop-perçu ({formatMontant(tropPercu)}) sur le mois suivant
                          </span>
                          <p className="text-xs text-gray-600 mt-0.5">
                            Le crédit sera automatiquement déduit du montant à payer du mois prochain
                          </p>
                        </div>
                      </label>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ÉTAPE 6 : PAIEMENT */}
            {moisSelectionne && paiementDetails && 
             !paiementDetails?.paiement?.prorata_applique && 
             paiementDetails?.paiement?.statut_frequentation !== 'non_scolarise' && (
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <span className="bg-[#246990] text-white w-8 h-8 rounded-full flex items-center justify-center text-sm">
                    {montantDejaPaye > 0 && tropPercu > 0 ? '7' : '6'}
                  </span>
                  Paiement
                </h3>

                {/* Checkbox "Marquer comme payé" */}
                <label className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg cursor-pointer hover:bg-green-100 transition-colors">
                  <div 
                    className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                      marquerCommePayé
                        ? 'bg-[#246990] border-[#246990]'
                        : 'border-gray-300'
                    }`}
                    onClick={() => setMarquerCommePayé(!marquerCommePayé)}
                  >
                    {marquerCommePayé && (
                      <Check size={14} className="text-white" />
                    )}
                  </div>
                  <div className="flex-1">
                    <span className="text-sm font-semibold text-gray-800">
                      Enregistrer le paiement du montant prorata ({formatMontant(montantProrata)})
                    </span>
                    <p className="text-xs text-gray-600 mt-0.5">
                      Le statut passera automatiquement à "Payé au prorata"
                    </p>
                  </div>
                </label>

                {/* Sélecteur mode de paiement */}
                {marquerCommePayé && (
                  <div className="mt-3 p-3 bg-white border border-gray-300 rounded-lg space-y-3">
                    <div>
                      <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                        <CreditCard size={16} />
                        Mode de paiement <span className="text-red-500">*</span>
                      </label>
                      {/* Le <select> natif était le dernier de ce modal : il ne
                          suivait ni la hauteur, ni le rayon, ni la couleur de focus
                          des autres champs de l'application. */}
                      <Select value={modePaiementId} onValueChange={setModePaiementId}>
                        <SelectTrigger className="w-full h-9 rounded-lg border-gray-200 text-sm focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15">
                          <SelectValue placeholder="Sélectionnez un mode de paiement" />
                        </SelectTrigger>
                        <SelectContent>
                          {modesPaiement.map(mode => (
                            <SelectItem key={mode.id} value={String(mode.id)}>{mode.nom}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Référence de paiement (optionnel)
                      </label>
                      <input
                        type="text"
                        value={referencePaiement}
                        onChange={(e) => setReferencePaiement(e.target.value)}
                        placeholder="Ex: Reçu #1234, Virement du 29/01"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-[#246990] focus:outline-none text-sm"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* RÉCAPITULATIF */}
            {peutEnregistrer() && (
              <div className="border-t pt-6">
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <h4 className="font-semibold text-sm text-green-800 mb-3 flex items-center gap-2">
                    <CheckCircle size={16} />
                    Récapitulatif
                  </h4>
                  <div className="space-y-2 text-xs">
                    <div className="flex justify-between">
                      <span>Type :</span>
                      <span className="font-semibold">{typeSelection === 'eleve' ? 'Élève individuel' : 'Famille'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Mois :</span>
                      <span className="font-semibold">{moisScolaires.find(m => m.id === moisSelectionne)?.nom}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Mode de calcul :</span>
                      <span className="font-semibold capitalize">
                        {modeCalcul === 'pourcentage' && `${pourcentage}%`}
                        {modeCalcul === 'periode' && 'Période'}
                        {modeCalcul === 'montant_fixe' && 'Montant fixe'}
                      </span>
                    </div>
                    <div className="flex justify-between pt-2 border-t border-green-300">
                      <span>Montant normal :</span>
                      <span className="font-semibold">{formatMontant(montantNormal)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Montant prorata :</span>
                      <span className="font-semibold text-[#246990]">{formatMontant(montantProrata)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Économie :</span>
                      <span className="font-semibold text-green-600">-{formatMontant(montantNormal - montantProrata)}</span>
                    </div>
                    {tropPercu > 0 && reporterTropPercu && (
                      <div className="flex justify-between pt-2 border-t border-green-300">
                        <span>Trop-perçu reporté :</span>
                        <span className="font-semibold text-orange-600">{formatMontant(tropPercu)}</span>
                      </div>
                    )}
                    {marquerCommePayé && (
                      <div className="flex justify-between pt-2 border-t border-green-300">
                        <span className="font-bold">Action :</span>
                        <span className="font-bold text-blue-700">Paiement enregistré</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-gray-50 p-4 border-t flex justify-between items-center">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={saving}
              className="px-6 py-2"
            >
              Fermer
            </Button>

            <div className="flex items-center gap-3">
              {!peutEnregistrer() && !paiementDetails?.paiement?.prorata_applique && moisSelectionne && (
                <span className="text-sm text-red-600">Remplissez tous les champs</span>
              )}
              
              {!paiementDetails?.paiement?.prorata_applique && paiementDetails?.paiement?.statut_frequentation !== 'non_scolarise' && (
                <Button
                  onClick={handleSave}
                  disabled={saving || !peutEnregistrer()}
                  className="bg-[#246990] hover:bg-[#246990] text-white px-6 py-2 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? (
                    <>
                      <Loader2 className="animate-spin" size={18} />
                      Enregistrement...
                    </>
                  ) : (
                    marquerCommePayé ? 'Enregistrer prorata et paiement' : 'Enregistrer le prorata'
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Les messages de ce modal expliquent un refus - « ce mois est déjà réglé,
          appliquer un prorata en réécrira le montant » - et un bandeau étroit les
          tronquait. shouldUseLongToast arbitre : long au-delà de 130 caractères. */}
      <div className="fixed top-4 right-4 z-[10000] space-y-2">
        {toasts.map(toast => (
          shouldUseLongToast(toast.message, toast.type) ? (
            <LongToast
              key={toast.id}
              show
              message={toast.message}
              type={toast.type}
              onClose={() => removeToast(toast.id)}
            />
          ) : (
            <Toast
              key={toast.id}
              message={toast.message}
              type={toast.type}
              onClose={() => removeToast(toast.id)}
            />
          )
        ))}
      </div>

     

      {/* ConfirmDialog pour l'annulation */}
      {showConfirmDialog && (
        <ConfirmDialog
          isOpen={showConfirmDialog}
          onClose={() => setShowConfirmDialog(false)}
          onConfirm={() => {
            setShowConfirmDialog(false);
            if (confirmAction) confirmAction();
          }}
          title="Annuler le prorata"
          message="Êtes-vous sûr de vouloir annuler le prorata appliqué sur ce mois ? Le montant attendu sera restauré à sa valeur d'origine."
          confirmText="Annuler le prorata"
          cancelText="Fermer"
        />
      )}
    </>
  );
};

export default ModalPaiementProrata;